/*
  Evirelle motion: small, purposeful touches.
  - Boxed content blocks fade up once as they enter (one unit each, never every item).
  - Before/after slider: the handle glides left and right once, to show it can be dragged.
  - USP hotspots: dots pop in one by one, then the dashed lines appear and keep flowing.
  Nothing is hidden unless this script runs, and visitors who prefer reduced motion get no animation.
*/
(() => {
  if (!('IntersectionObserver' in window) || window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const onceVisible = (element, callback, threshold = 0.45) => {
    const observer = new IntersectionObserver((entries) => {
      if (!entries[0].isIntersecting) return;
      observer.disconnect();
      callback();
    }, { threshold });
    observer.observe(element);
  };

  /* Before/after: one gentle hint of the drag, cancelled as soon as the visitor touches it */
  const hintBeforeAfter = (widget) => {
    if (widget.dataset.eviHinted) return;
    widget.dataset.eviHinted = '';

    const range = widget.querySelector('[data-ba-range]');
    if (!range) return;

    let cancelled = false;
    const cancel = () => { cancelled = true; };
    widget.addEventListener('pointerdown', cancel, { once: true });
    range.addEventListener('keydown', cancel, { once: true });

    onceVisible(widget, () => {
      const start = parseFloat(range.value) || 50;
      const keyframes = [start, start - 12, start + 10, start];
      const segment = 650;
      const ease = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
      let segmentIndex = 0;
      let segmentStart = null;

      const step = (now) => {
        if (cancelled) return;
        if (segmentStart === null) segmentStart = now;

        const t = Math.min(1, (now - segmentStart) / segment);
        const from = keyframes[segmentIndex];
        const to = keyframes[segmentIndex + 1];
        const value = from + (to - from) * ease(t);

        range.value = value;
        widget.style.setProperty('--ba-position', `${value}%`);

        if (t === 1) {
          segmentIndex += 1;
          segmentStart = null;
          if (segmentIndex >= keyframes.length - 1) return;
        }

        requestAnimationFrame(step);
      };

      setTimeout(() => requestAnimationFrame(step), 300);
    }, 0.6);
  };

  /* Boxed content blocks fade up once, as one unit (never individual items or full-bleed sections) */
  const boxedSelectors = [
    '.product-slider__header',
    '.product-slider__viewport',
    '.feature-cards__heading',
    '.feature-cards__list',
    '.faq-accordion__content',
    '.review-masonry__header',
    '.review-masonry__grid',
    '.before-after-slider__header',
    '.before-after-slider__widget'
  ].join(',');

  document.documentElement.classList.add('evi-motion');

  const revealObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      entry.target.classList.add('is-revealed');
      revealObserver.unobserve(entry.target);
    });
  }, { rootMargin: '0px 0px -8% 0px', threshold: 0.08 });

  const revealBoxed = (scope, skipVisible) => {
    scope.querySelectorAll(boxedSelectors).forEach((element) => {
      if (element.hasAttribute('data-evi-reveal')) return;
      // Already on screen at load: leave it, so nothing flashes
      if (skipVisible && element.getBoundingClientRect().top < window.innerHeight) return;
      element.setAttribute('data-evi-reveal', '');
      revealObserver.observe(element);
    });
  };

  /* USP hotspots: dots pop in one by one, then the dashed lines fade in and keep flowing */
  const playHotspots = (stage, skipVisible) => {
    if (stage.hasAttribute('data-evi-hotspots')) return;
    if (skipVisible && stage.getBoundingClientRect().top < window.innerHeight * 0.85) return;

    stage.setAttribute('data-evi-hotspots', '');
    stage.querySelectorAll('.usp-hotspots__dot').forEach((dot, index) => {
      dot.style.setProperty('--evi-dot-delay', `${index * 120}ms`);
    });

    onceVisible(stage, () => stage.classList.add('is-playing'), 0.3);
  };

  const scan = (scope, skipVisible = false) => {
    scope.querySelectorAll('[data-ba-widget]').forEach(hintBeforeAfter);
    scope.querySelectorAll('.usp-hotspots__stage').forEach((stage) => playHotspots(stage, skipVisible));
    revealBoxed(scope, skipVisible);
  };

  const start = () => scan(document, true);
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  document.addEventListener('shopify:section:load', (event) => scan(event.target, false));
})();
